"use client";

import { currencyFormatter, Vehicle, VehicleFeature, VehicleFilter } from "@alba-cars/common-modules";
import { AnimatePresence, motion, Variants } from "framer-motion";
import { MinusIcon, X } from "lucide-react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import React, { useState } from "react";

import CarNavigationButton from "./CarNavigationButton";
import CompareCarFilters from "./CompareCarFilters";
import { FilterProvider } from "@/app/[locale]/buy-used-cars-uae/_components/context/FilterContext";
import Icon from "@/components/Icons/Icon";
import IconCheck from "@/components/Icons/IconCheck";
import IconFirstStar from "@/components/Icons/IconFirstStar";
import IconPlus from "@/components/Icons/IconPlus";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Link } from "@/i18n/routing";
import { fetchCars } from "@/lib/api/client/fetchCars";
import { fetchSimilarCars } from "@/lib/api/compare-vehicle";
import { formatImageUrl, getCarMonthlyInstallment, getCarPrice, slugify } from "@/lib/utils";
import { DEFAULT_PLACEHOLDER } from "@/utils/constants";
import { getCarNameTitle } from "@/utils/utils";

// Change this to change how many comparison results are shown
const SHOW_MAX_CARS = 5;

// Animation variants
const containerVariants :Variants= {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      staggerChildren: 0.05,
      staggerDirection: -1,
    },
  },
};

const cardVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 20,
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    scale: 0.95,
    transition: {
      duration: 0.3,
    },
  },
};

const sunroofOptions = ["Dual Sunroof", "Sunroof", "Rear Sunroof", "Panoramic Sunroof"];

const CompareCars = ({ car }: { car: Vehicle }) => {
  const t = useTranslations("CarDetails.compare");
  const tGallery = useTranslations("CarDetails.gallery");
  const tFeatures = useTranslations("CarDetails.features");

  const [similarCars, setSimilarCars] = useState<Map<string, Vehicle>>(new Map());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filterSheetOpen, setFilterSheetOpen] = useState<boolean>(false);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const loadSimilarCars = async (carId: string) => {
    try {
      setIsLoading(true);
      const cars = await fetchSimilarCars(carId);
      setSimilarCars(new Map(cars?.map((car) => [car.id, car])));
    } catch (error) {
      console.error("Error fetching similar cars:", error);
      setSimilarCars(new Map());
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    loadSimilarCars(car.id);
  }, [car.id]);

  const handleFilterChange = async (newFilterValues: Partial<VehicleFilter>): Promise<Vehicle[] | undefined> => {
    setIsLoading(true);
    newFilterValues.compareCarId = car.id;
    try {
      const result = await fetchCars({
        filter: newFilterValues,
        options: {
          page: 1,
          limit: 10,
          withFinance: true,
          withFeatures: true,
        },
      });
      const newCars = result.data?.result || [];

      let similarCarsArray: Vehicle[];

      if (newCars.length > 5) {
        //* If there are more than 5 cars found by filtering, we remove the default similar cars
        similarCarsArray = [];
      } else {
        similarCarsArray = Array.from(similarCars.values());
      }

      //* we want to insert the new cars (query result) first, before the default similar cars
      const updatedArray = [...newCars, ...similarCarsArray];
      const updatedSimilarCars = new Map(updatedArray.map((car) => [car.id, car]));

      setSimilarCars((_prev) => updatedSimilarCars);

      return result.data?.result;
    } catch (error) {
      console.error("Error applying filters:", error);
      return undefined;
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetFilter = async (): Promise<void> => {
    setIsLoading(true);
    await loadSimilarCars(car.id);
  };

  const hasFeature = (features: VehicleFeature[] | undefined, names: string | string[]) => {
    let hasOption = false;
    const namesArray = Array.isArray(names) ? names : [names];

    if (namesArray.includes("Sunroof")) {
      hasOption = features?.some((v) => sunroofOptions.includes(v.name)) ?? false;
    } else {
      hasOption = features?.some((v) => namesArray.includes(v.name)) ?? false;
    }

    return hasOption ? (
      <Icon
        icon={<IconCheck fill="white" bg="transparent" width="30" height="30" />}
        backgroundColor="success-lighter"
        height={5}
        width={5}
      />
    ) : (
      <Icon icon={<MinusIcon color="white" />} backgroundColor="primary" height={5} width={5} />
    );
  };

  const handleRemoveCar = async (carId: string) => {
    const newSimilarCars = new Map(similarCars);
    newSimilarCars.delete(carId);

    setSimilarCars((_prev) => newSimilarCars);

    if (!newSimilarCars.size) {
      //* if you delete the last car, we want to show the default similar cars again
      //* And we also clear the filters
      await loadSimilarCars(car.id);
      await handleResetFilter();
    }
  };

  const renderCarCard = (vehicle: Vehicle, isMainCar: boolean = false, index: number) => {
    const carPrice = getCarPrice(vehicle);
    const installment = getCarMonthlyInstallment(vehicle);
    const carName = getCarNameTitle(vehicle, { showTrim: true });

    return (
      <motion.div
        key={vehicle.id}
        custom={index}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        layout
        className="relative flex h-full w-[180px] flex-col justify-between rounded-3xl bg-white p-1 md:min-w-[260px] lg:min-w-[350px] lg:max-w-fit lg:flex-1"
      >
        {!isMainCar ? (
          <div className="absolute right-2 top-2 z-10">
            <button
              className="group rounded-full bg-white p-2 transition-all duration-300 hover:cursor-pointer hover:bg-secondary-light disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => handleRemoveCar(vehicle.id)}
            >
              <X className="h-5 w-5 text-primary-foreground transition-all duration-300 group-hover:text-primary" />
            </button>
          </div>
        ) : null}
        <Link
          href={`/buy-used-cars/vehicle/${vehicle.refId}-${slugify(vehicle.make.name)}-${slugify(vehicle.model.name)}-${slugify(vehicle.trim)}`}
        >
          <div className="relative h-32 lg:h-52">
            <Image
              fill
              priority
              quality={75}
              sizes="(max-width: 768px) 300px, (max-width: 1200px) 400px, 500px"
              src={formatImageUrl(vehicle.media?.[0]?.mediaUrl, { addAutoFormat: true }) || DEFAULT_PLACEHOLDER}
              alt={carName}
              className="rounded-3xl object-cover object-bottom transition-transform duration-700"
            />
          </div>
          <div className="pl-2 pt-3 lg:p-4">
            <div className="inter flex items-center justify-between text-base">
              <span className="max-w-54 line-clamp-2 h-11 text-wrap font-semibold hover:text-primary">{carName}</span>
              <span className="font-bold"></span>
            </div>
            <div className="inter my-5 flex flex-col justify-between border-b-2 border-secondary pb-5 lg:flex-row">
              {!!installment && (
                <div className="flex flex-col justify-start space-y-1">
                  <span className="text-xs font-medium text-[#898989]">{t("startsFrom")}</span>
                  <span className="text-sm font-bold text-primary">
                    {currencyFormatter({ isDecimal: true, decimalPlaces: 0 }).format(installment)}
                    {tGallery("perMonth")}
                  </span>
                </div>
              )}
              <div className="mt-3 flex flex-col justify-end space-y-1 lg:mt-0">
                <span className="text-xs font-medium text-[#898989]">{t("fullPrice")}</span>
                <span className="text-sm font-bold text-black">
                  {!!carPrice && currencyFormatter({ isDecimal: true, decimalPlaces: 0 }).format(carPrice)}
                </span>
              </div>
            </div>
            <div className="pe-2">
              <CarNavigationButton isMainCar={isMainCar} car={vehicle} />
            </div>
          </div>
        </Link>

        <div className="my-3 flex flex-col space-y-2 px-3">
          <span className="border-t-2 border-secondary pt-2 font-inter text-xs font-medium text-primary-foreground lg:text-sm">
            {currencyFormatter({ isDecimal: true, decimalPlaces: 0 }).format(carPrice)}
          </span>
          <span className="border-t-2 border-secondary pt-2 font-inter text-xs font-medium text-primary-foreground lg:text-sm">
            {vehicle.year}
          </span>
          <span className="border-t-2 border-secondary pt-2 font-inter text-xs font-medium text-primary-foreground lg:text-sm">
            {vehicle.mileage?.toLocaleString()} {t("km")}
          </span>
          <span className="border-t-2 border-secondary pt-2 font-inter text-xs font-medium text-primary-foreground lg:text-sm">
            {vehicle.warranty ? vehicle.warranty : tFeatures("underWarranty")}
          </span>
          <span className="border-t-2 border-secondary pt-2 font-inter text-xs font-medium text-primary-foreground lg:text-sm">
            {vehicle.transmission}
          </span>
          <span className="border-t-2 border-secondary pt-2 font-inter text-xs font-medium text-primary-foreground lg:text-sm">
            {vehicle.engineCapacity || t("standard")}
          </span>
          <span className="border-t-2 border-secondary pt-2 font-inter text-xs font-medium text-primary-foreground lg:text-sm">
            {vehicle.fuelType || t("petrol")}
          </span>
          <span className="h-[2.7rem] border-t-2 border-secondary pt-2 lg:h-fit">
            {hasFeature(vehicle.features, ["Rear Camera", "360 Camera"])}
          </span>
          <span className="h-[2.7rem] border-t-2 border-secondary pt-2 lg:h-fit">
            {hasFeature(vehicle.features, "Parking Sensors")}
          </span>
          <span className="h-[2.7rem] border-t-2 border-secondary pt-2 lg:h-fit">
            {hasFeature(vehicle.features, "Leather Seats")}
          </span>
          <span className="border-t-2 border-secondary pt-2">{hasFeature(vehicle.features, "Sunroof")}</span>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="relative mb-20 flex w-[100vw] gap-x-2 overflow-x-hidden md:mx-auto md:w-[80vw] lg:w-[94vw] lg:gap-x-4 xxxl:mx-auto xxxl:w-[90vw] xxxl:max-w-[80vw] ultrawide:w-[90vw] ultrawide:max-w-screen-2xl megawide:w-[1900px]">
      <div className="flex h-[837px] max-w-[100px] flex-col justify-between rounded-2xl bg-white p-4 lg:h-[900px] lg:min-w-[200px]">
        <div className="max-w-[150px] text-wrap">
          {isMobile ? (
            <button className="" onClick={() => setFilterSheetOpen(true)}>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-light">
                <IconPlus className="h-4 w-4 text-primary" fill="hsla(var(--primary))" />
              </div>
            </button>
          ) : (
            <>
              <IconFirstStar fillColor="hsla(var(--primary))" />
              <p className="mt-2 font-urbanist text-base font-semibold text-primary-foreground lg:text-2xl">
                {t("title")}
              </p>
            </>
          )}
        </div>

        <div className="flex flex-col space-y-2">
          <span className="border-t-2 border-secondary pt-2 font-inter text-xs font-medium text-primary-foreground lg:text-sm">
            {t("price")}
          </span>
          <span className="border-t-2 border-secondary pt-2 font-inter text-xs font-medium text-primary-foreground lg:text-sm">
            {t("year")}
          </span>
          <span className="border-t-2 border-secondary pt-2 font-inter text-xs font-medium text-primary-foreground lg:text-sm">
            {t("mileage")}
          </span>
          <span className="border-t-2 border-secondary pt-2 font-inter text-xs font-medium text-primary-foreground lg:text-sm">
            {t("warranty")}
          </span>
          <span className="border-t-2 border-secondary pt-2 font-inter text-xs font-medium text-primary-foreground lg:text-sm">
            {t("transmission")}
          </span>
          <span className="border-t-2 border-secondary pt-2 font-inter text-xs font-medium text-primary-foreground lg:text-sm">
            {t("engineSize")}
          </span>
          <span className="border-t-2 border-secondary pt-2 font-inter text-xs font-medium text-primary-foreground lg:text-sm">
            {t("fuelType")}
          </span>
          <span className="h-[2.7rem] border-t-2 border-secondary pt-2 font-inter text-xs font-medium text-primary-foreground lg:h-fit lg:text-sm">
            {t("rearCamera")}
          </span>
          <span className="h-[2.7rem] border-t-2 border-secondary pt-2 font-inter text-xs font-medium text-primary-foreground lg:h-fit lg:text-sm">
            {t("parkingSensors")}
          </span>
          <span className="h-[2.7rem] border-t-2 border-secondary pt-2 font-inter text-xs font-medium text-primary-foreground lg:h-fit lg:text-sm">
            {t("leatherSeats")}
          </span>
          <span className="border-t-2 border-secondary pt-2 font-inter text-xs font-medium text-primary-foreground lg:text-sm">
            {t("sunroof")}
          </span>
        </div>
      </div>

      <div className="!no-scrollbar overflow-x-auto pb-4 lg:flex-1">
        <div className="flex h-[840px] min-w-[100%] gap-x-4 lg:h-[900px] lg:min-w-[700px]">
          {/* Main car doesn't need animation */}
          <div className="thin-scrollbar flex max-w-full gap-2 overflow-x-auto overflow-y-hidden lg:gap-4">
            <div className="flex">{renderCarCard(car, true, -1)}</div>
            {/* Loading state */}
            {isLoading ? (
              <div className="flex items-center justify-center lg:min-w-[350px]">
                <motion.div
                  animate={{
                    rotate: 360,
                    transition: { duration: 1, repeat: Infinity, ease: "linear" },
                  }}
                  className="h-12 w-12 rounded-full border-4 border-primary border-t-transparent"
                />
              </div>
            ) : (
              /* Animated car list */
              <AnimatePresence mode="popLayout">
                <motion.div
                  className="flex gap-x-2 lg:gap-x-4"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  key={Array.from(similarCars.values())
                    .map((car) => car.id)
                    .join("-")}
                >
                  {Array.from(similarCars.values())
                    .slice(0, SHOW_MAX_CARS)
                    .map((similarCar, index) => renderCarCard(similarCar, false, index))}
                </motion.div>
              </AnimatePresence>
            )}
            <div className="min-w-[50px] md:hidden md:min-w-[80px]"></div>
          </div>

          <FilterProvider searchParams={{}}>
            {/* Pass the open state to CompareCarFilters for mobile */}
            <CompareCarFilters
              handleFilterChange={handleFilterChange}
              handleResetFilter={handleResetFilter}
              open={filterSheetOpen}
              setOpen={setFilterSheetOpen}
            />
          </FilterProvider>
        </div>
      </div>
    </div>
  );
};

export default CompareCars;
